import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";

// constants
import { textValues } from "./constants";

// components
import Button from "@/components/shared/Button";

// mui
import {
    Box,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";

// utils
import { toasterMessage as TM } from "@/utils/toaster-msg";
import { getMustVisitPayload } from "@/utils/students";
import { getRegistrantCodeOptions, getUniversitiesFromStudent } from "./util";

// actions
import { showToasterMessage } from "@/store/actions/commonActions";
import { updateMustVisitUniversity } from "@/store/actions/studentDetailsActions";

// style
import { useStyles } from "./style";

export default function CopyMustVistForm({
    loadAllMustVistCell,
    setLoadAllMustVistCell,
    registrantCode,
    rows,
}) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const students = useSelector((state) => state.studentDetails.data);

    const [applied, setApplied] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);

    const updatedRows = useMemo(
        () => rows.map((item, index) => ({ ...item, id: index })),
        [rows]
    );

    const registrantCodeOptions = useMemo(
        () => getRegistrantCodeOptions(registrantCode, students),
        [registrantCode, students]
    );

    const handleSelect = (event) => {
        const data = event.target.value;
        setSelectedStudent(data);

        if (data && data.registrantCode !== selectedStudent.registrantCode) {
            setApplied(false);
        }
    };

    const handleUnselect = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedStudent(null);
    };

    const copyMustVisitCallback = (isLoading, error) => {
        setLoadAllMustVistCell(isLoading);
        if (error) {
            dispatch(showToasterMessage(TM.cantUpdate.msg, TM.cantUpdate.color));
            return;
        }

        setApplied(true);
        dispatch(showToasterMessage(TM.reportUpdated.msg, TM.reportUpdated.color));
    };

    const handleCopyMustVisit = () => {
        setLoadAllMustVistCell(true);
        const universities = getUniversitiesFromStudent(selectedStudent, students);
        const payload = getMustVisitPayload(universities, registrantCode, false);

        if (payload) {
            dispatch(
                updateMustVisitUniversity(updatedRows, payload, copyMustVisitCallback)
            );
            return;
        }

        setLoadAllMustVistCell(false);
        dispatch(showToasterMessage(TM.cantUpdate.msg, TM.cantUpdate.color));
    };

    const renderValue = (item, lineClamp = false) => {
        const isActiveValue = selectedStudent
            ? item.registrantCode === selectedStudent.registrantCode
            : false;

        return (
            <Box display="flex" alignItems="center" overflow="hidden" gridGap="4px">
                <span
                    className={`${classes.pill} ${isActiveValue ? classes.activePill : ""
                        }`}
                >
                    <span>{item.registrantCode}</span>
                </span>
                <span>-</span>
                <span
                    className={`${classes.name} ${lineClamp ? classes.nameLineClamp : ""
                        }`}
                >
                    {item.name}
                </span>
            </Box>
        );
    };

    if (isEmpty(registrantCodeOptions)) {
        return null;
    }

    return (
        <Box className={classes.root}>
            <Typography variant="h4" color="primary">
                {textValues.mustVistFormErrorLabel}
            </Typography>

            <Select
                value={selectedStudent}
                onChange={handleSelect}
                className={classes.formControl}
                variant="outlined"
                classes={{ select: `${classes.select} ${classes.label}` }}
                renderValue={renderValue}
                endAdornment={
                    selectedStudent && (
                        <InputAdornment style={{ marginRight: "12px" }} position="end">
                            <IconButton onClick={handleUnselect} size="small">
                                <CancelIcon classes={{ root: classes.icon }} />
                            </IconButton>
                        </InputAdornment>
                    )
                }
            >
                {registrantCodeOptions.map((item, index) => (
                    <MenuItem
                        key={index}
                        value={item}
                        classes={{ root: `${classes.menuItem} ${classes.label}` }}
                    >
                        {renderValue(item, true)}
                    </MenuItem>
                ))}
            </Select>

            {selectedStudent && (
                <Button
                    label={applied ? textValues.appliedLabel : textValues.applyLabel}
                    color={"success"}
                    disabled={loadAllMustVistCell || applied}
                    fullWidth={isMobile}
                    onClick={handleCopyMustVisit}
                />
            )}
        </Box>
    );
}

CopyMustVistForm.propTypes = {
    loadAllMustVistCell: PropTypes.bool,
    setLoadAllMustVistCell: PropTypes.func.isRequired,
    rows: PropTypes.array.isRequired,
    registrantCode: PropTypes.string.isRequired,
};
